import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Tag,
  Calendar,
  Percent,
  DollarSign,
  Users,
  Building2,
  Gift,
  Share2,
  Copy,
  MessageSquare,
  Pencil,
  Trash2,
  Eye,
  Upload,
  ImageOff,
} from "lucide-react";

import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { MultiSelect, MultiSelectOption } from "./ui/multi-select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";

import { couponsApi, organizationsApi, userPlansApi, ApiError } from "../utils/api";
import {
  StatusBadge,
  TabIconBadge,
  ErrorBanner,
  formatDate,
  TAB_ACCENTS,
} from "./StaticDataManager";
import { CouponPreview } from "./CouponPreview";

interface CouponDetailsProps {
  couponId: string | null;
  mode: "view" | "edit" | "create";
  kind?: "discount" | "free-grant";
  onBack: () => void;
}

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof ApiError ? err.message : fallback;

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toDatetimeLocal = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromDatetimeLocal = (value: string) =>
  value ? new Date(value).toISOString() : undefined;

const emptyForm = {
  orgcoupon_name: "",
  coupon_code: "",
  description: "",
  organizationIds: [] as string[],
  starts_at: "",
  expires_at: "",
  discountType: "%",
  discountPercent: "",
  discountAmount: "",
  usage_limit: "",
  couponMessage: "",
  thumbnailPath: "",
  rulesApplicablePlans: [] as string[],
  rulesPerUserLimit: "",
  rulesMinimumOrderValue: "",
  targetAudience: "",
  licenses: "",
  dataLimitGB: "",
  durationDays: "",
  freeGrantPlanId: "",
};

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 text-sm border-b last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

function RequiredLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <Label htmlFor={htmlFor}>
      {children} <span className="text-destructive">*</span>
    </Label>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive">{message}</p>;
}

export function CouponDetails({ couponId, mode, kind, onBack }: CouponDetailsProps) {
  const isCreate = mode === "create";
  const isEdit = mode === "edit";

  const [coupon, setCoupon] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(!isCreate);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [orgNames, setOrgNames] = useState<Record<string, string>>({});
  const [planNames, setPlanNames] = useState<Record<string, string>>({});
  const [orgOptions, setOrgOptions] = useState<MultiSelectOption[]>([]);
  const [planOptions, setPlanOptions] = useState<MultiSelectOption[]>([]);
  // Free Grant Plan only makes sense pointed at an organization-type plan,
  // and a disabled plan shouldn't be offered as a new grant - separate from
  // planOptions above, which backs "Applicable Plans" and needs every plan
  // type for discount coupons.
  const [freeGrantPlanOptions, setFreeGrantPlanOptions] = useState<MultiSelectOption[]>([]);

  const [form, setForm] = useState({ ...emptyForm });
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearFieldError = (field: string) =>
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });

  const isFreeGrant = isCreate ? kind === "free-grant" : !!coupon?.isFreeGrant;

  const goView = (id: string = couponId || "") => {
    window.location.hash = `coupon-management/${id}`;
  };
  const goEdit = () => {
    window.location.hash = `coupon-management/${couponId}/edit`;
  };

  const populateForm = (c: any) => {
    setForm({
      orgcoupon_name: c.orgcoupon_name || "",
      coupon_code: c.coupon_code || "",
      description: c.description || "",
      organizationIds: c.organizationIds || [],
      starts_at: toDatetimeLocal(c.starts_at),
      expires_at: toDatetimeLocal(c.expires_at),
      discountType: c.discountType || "%",
      discountPercent: c.discountPercent != null ? String(c.discountPercent) : "",
      discountAmount: c.discountAmount != null ? String(c.discountAmount) : "",
      usage_limit: c.usage_limit != null ? String(c.usage_limit) : "",
      couponMessage: c.couponMessage || "",
      thumbnailPath: c.thumbnailPath || "",
      rulesApplicablePlans: c.rules?.applicablePlans || [],
      rulesPerUserLimit: c.rules?.perUserLimit != null ? String(c.rules.perUserLimit) : "",
      rulesMinimumOrderValue:
        c.rules?.minimumOrderValue != null ? String(c.rules.minimumOrderValue) : "",
      targetAudience: c.targetAudience || "",
      licenses: c.licenses != null ? String(c.licenses) : "",
      dataLimitGB: c.dataLimitGB != null ? String(c.dataLimitGB) : "",
      durationDays: c.durationDays != null ? String(c.durationDays) : "",
      freeGrantPlanId: c.freeGrantPlanId || "",
    });
  };

  const load = async () => {
    if (!couponId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await couponsApi.getById(couponId);

      if (response.success) {
        setCoupon(response.data);
        populateForm(response.data);
      } else {
        setError(response.message || "Failed to load coupon");
      }
    } catch (err) {
      setError(errorMessage(err, "Failed to load coupon"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setFieldErrors({});
    if (!isCreate) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponId, mode]);

  // Org/plan lists are only needed for display names and the form's pickers -
  // resolve them best-effort so the page still works (falling back to raw
  // ids) if these calls fail.
  useEffect(() => {
    (async () => {
      try {
        const [orgsRes, plansRes, freeGrantPlansRes] = await Promise.all([
          organizationsApi.getAll({ limit: 200 }),
          userPlansApi.getAll({ limit: 200 }),
          userPlansApi.getAll({ limit: 200, userType: "organization", disabled: false }),
        ]);

        if (orgsRes.success) {
          const orgs = orgsRes.data.organizations || [];
          const map: Record<string, string> = {};
          orgs.forEach((org: any) => {
            map[org._id] = org.organizationName || org.name || org._id;
          });
          setOrgNames(map);
          setOrgOptions(
            orgs.map((org: any) => ({
              value: org._id,
              label: org.organizationName || org.name || org._id,
            }))
          );
        }

        if (plansRes.success) {
          const plans = plansRes.data.plans || [];
          const map: Record<string, string> = {};
          plans.forEach((plan: any) => {
            map[plan._id] = plan.planName || plan._id;
          });
          setPlanNames(map);
          setPlanOptions(
            plans.map((plan: any) => ({
              value: plan._id,
              label: plan.planName || plan._id,
            }))
          );
        }

        if (freeGrantPlansRes.success) {
          const orgPlans = freeGrantPlansRes.data.plans || [];
          setFreeGrantPlanOptions(
            orgPlans.map((plan: any) => ({
              value: plan._id,
              label: plan.planName || plan._id,
            }))
          );
        }
      } catch {
        // best-effort only
      }
    })();
  }, []);

  const copyCode = async () => {
    if (!coupon?.coupon_code) return;
    try {
      await navigator.clipboard.writeText(coupon.coupon_code);
      toast.success("Coupon code copied");
    } catch {
      toast.error("Failed to copy code");
    }
  };

  const handleThumbnailChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadingThumbnail(true);
    try {
      const response = await couponsApi.uploadThumbnail(file);
      if (response.success) {
        setForm((current) => ({ ...current, thumbnailPath: response.data.thumbnailPath }));
        toast.success("Thumbnail uploaded");
      } else {
        toast.error(response.message || "Failed to upload thumbnail");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to upload thumbnail"));
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!form.orgcoupon_name.trim()) errors.orgcoupon_name = "Coupon name is required";
    if (!form.coupon_code.trim()) {
      errors.coupon_code = "Coupon code is required";
    } else if (form.coupon_code.trim().length < 3 || form.coupon_code.trim().length > 15) {
      errors.coupon_code = "Coupon code must be between 3-15 characters";
    }
    if (!form.starts_at) errors.starts_at = "Start date is required";
    if (!form.expires_at) {
      errors.expires_at = "Expiry date is required";
    } else if (form.starts_at && new Date(form.expires_at) <= new Date(form.starts_at)) {
      errors.expires_at = "Expiry date must be after start date";
    }

    if (isFreeGrant) {
      if (!form.licenses) errors.licenses = "Licenses is required for free-grant coupons";
      if (!form.dataLimitGB) errors.dataLimitGB = "Data limit (GB) is required for free-grant coupons";
      if (!form.durationDays) errors.durationDays = "Duration (days) is required for free-grant coupons";
    } else if (form.discountType === "%") {
      if (!form.discountPercent) errors.discountPercent = "Discount percent is required for percentage coupons";
    } else if (!form.discountAmount) {
      errors.discountAmount = "Discount amount is required for flat coupons";
    }

    return errors;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      toast.error(Object.values(validationErrors)[0]);
      return;
    }
    setFieldErrors({});

    setSaving(true);

    try {
      const payload: any = {
        orgcoupon_name: form.orgcoupon_name.trim(),
        coupon_code: form.coupon_code.trim().toUpperCase(),
        description: form.description.trim(),
        organizationIds: form.organizationIds,
        starts_at: fromDatetimeLocal(form.starts_at),
        expires_at: fromDatetimeLocal(form.expires_at),
        usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit),
        couponMessage: form.couponMessage.trim(),
        thumbnailPath: form.thumbnailPath,
      };

      if (isFreeGrant) {
        // Free grants aren't a checkout discount - 0% satisfies the shared
        // discount fields without doing anything at checkout.
        payload.isFreeGrant = true;
        payload.discountType = "%";
        payload.discountPercent = 0;
        // Free grants are always for onboarding new organizations - there's
        // no coach/athlete audience option.
        payload.targetAudience = "organization";
        payload.licenses = Number(form.licenses);
        payload.dataLimitGB = Number(form.dataLimitGB);
        payload.durationDays = Number(form.durationDays);
        payload.freeGrantPlanId = form.freeGrantPlanId || null;
      } else {
        payload.isFreeGrant = false;
        payload.discountType = form.discountType;
        payload.rules = {
          applicablePlans: form.rulesApplicablePlans,
          perUserLimit: form.rulesPerUserLimit === "" ? undefined : Number(form.rulesPerUserLimit),
          minimumOrderValue:
            form.rulesMinimumOrderValue === "" ? undefined : Number(form.rulesMinimumOrderValue),
        };

        if (form.discountType === "%") {
          payload.discountPercent = form.discountPercent ? Number(form.discountPercent) : 0;
        } else {
          payload.discountAmount = form.discountAmount ? Number(form.discountAmount) : 0;
        }
      }

      const response = isCreate
        ? await couponsApi.create(payload)
        : await couponsApi.update(couponId as string, payload);

      toast[response.success ? "success" : "error"](
        (response.message || (isCreate ? "Coupon created" : "Coupon updated")).replace(
          /\s*\(free grants are not synced to stripe\)\s*$/i,
          ""
        )
      );

      if (response.success) {
        if (isCreate) {
          goView(response.data._id);
        } else {
          setCoupon(response.data || { ...coupon, ...payload });
          goView();
        }
      }
    } catch (err) {
      if (err instanceof ApiError && err.fieldErrors) {
        setFieldErrors(err.fieldErrors);
      }
      toast.error(errorMessage(err, "Failed to save coupon"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!couponId) return;
    try {
      const response = await couponsApi.delete(couponId);
      toast[response.success ? "success" : "error"](response.message || "Coupon deleted");
      if (response.success) {
        setDeleteOpen(false);
        onBack();
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete coupon — try disabling it instead"));
    }
  };

  if (!isCreate && isLoading) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Coupons
        </Button>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Loading coupon details...
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isCreate && (error || !coupon)) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Coupons
        </Button>
        <ErrorBanner message={error || "Coupon not found"} onRetry={load} />
      </div>
    );
  }

  const discountDisplay = coupon
    ? coupon.isFreeGrant
      ? "Free"
      : coupon.discountType === "$"
        ? `$${coupon.discountAmount ?? 0}`
        : `${coupon.discountPercent ?? 0}%`
    : "";

  const rules = coupon?.rules || {};
  const applicablePlans: string[] = rules.applicablePlans || [];
  const organizationIds: string[] = coupon?.organizationIds || [];

  const formTitle = isCreate
    ? isFreeGrant
      ? "New Free Grant"
      : "New Discount Coupon"
    : "Edit Coupon";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Coupons
        </Button>

        {!isCreate && (
          <div className="flex items-center gap-2">
            {mode === "view" && (
              <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)}>
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>

            {mode === "view" ? (
              <Button size="sm" onClick={goEdit} className={TAB_ACCENTS.coupons.button}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => goView()}>
                <Eye className="h-4 w-4 mr-2" />
                Show
              </Button>
            )}
          </div>
        )}
      </div>

      {!isCreate && (
        <div className="flex items-start gap-3">
          <TabIconBadge icon={Tag} accent="coupons" />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold truncate">{coupon.orgcoupon_name}</h1>
              <StatusBadge disabled={!!coupon.disabled} />
            </div>
            <div className="mt-1 flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-xs">
                {coupon.coupon_code}
              </Badge>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={copyCode}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
            {coupon.description && (
              <p className="text-sm text-muted-foreground mt-1">{coupon.description}</p>
            )}
          </div>
        </div>
      )}

      {isCreate || isEdit ? (
        <Card className={TAB_ACCENTS.coupons.border}>
          <CardHeader>
            <CardTitle className="text-base">{formTitle}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <RequiredLabel htmlFor="coupon-name">Coupon Name</RequiredLabel>
                <Input
                  id="coupon-name"
                  value={form.orgcoupon_name}
                  aria-invalid={!!fieldErrors.orgcoupon_name}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, orgcoupon_name: event.target.value }));
                    clearFieldError("orgcoupon_name");
                  }}
                  placeholder="e.g. Summer Promo"
                />
                <FieldError message={fieldErrors.orgcoupon_name} />
              </div>

              <div className="space-y-1">
                <RequiredLabel htmlFor="coupon-code">Coupon Code</RequiredLabel>
                <Input
                  id="coupon-code"
                  value={form.coupon_code}
                  aria-invalid={!!fieldErrors.coupon_code}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, coupon_code: event.target.value }));
                    clearFieldError("coupon_code");
                  }}
                  placeholder="e.g. SUMMER25"
                  className="uppercase"
                  maxLength={15}
                />
                <FieldError message={fieldErrors.coupon_code} />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="coupon-description">Description</Label>
              <Textarea
                id="coupon-description"
                value={form.description}
                onChange={(event) =>
                  setForm((current) => ({ ...current, description: event.target.value }))
                }
                placeholder="Short description"
              />
            </div>

            {!isFreeGrant && (
              <div className="space-y-1">
                <Label>Organizations</Label>
                <MultiSelect
                  options={orgOptions}
                  selected={form.organizationIds}
                  onChange={(values) =>
                    setForm((current) => ({ ...current, organizationIds: values }))
                  }
                  placeholder="Global (all organizations)"
                  searchPlaceholder="Search organizations..."
                  emptyText="No organizations found."
                />
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <RequiredLabel htmlFor="coupon-starts">Starts At</RequiredLabel>
                <Input
                  id="coupon-starts"
                  type="datetime-local"
                  value={form.starts_at}
                  aria-invalid={!!fieldErrors.starts_at}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, starts_at: event.target.value }));
                    clearFieldError("starts_at");
                  }}
                />
                <FieldError message={fieldErrors.starts_at} />
              </div>

              <div className="space-y-1">
                <RequiredLabel htmlFor="coupon-expires">Expires At</RequiredLabel>
                <Input
                  id="coupon-expires"
                  type="datetime-local"
                  value={form.expires_at}
                  aria-invalid={!!fieldErrors.expires_at}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, expires_at: event.target.value }));
                    clearFieldError("expires_at");
                  }}
                />
                <FieldError message={fieldErrors.expires_at} />
              </div>
            </div>

            {isFreeGrant ? (
              <div className="space-y-3 rounded-md border p-3">
                <p className="text-sm font-medium">What this coupon grants</p>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-1">
                    <RequiredLabel htmlFor="grant-licenses">Licenses</RequiredLabel>
                    <Input
                      id="grant-licenses"
                      type="number"
                      min="1"
                      value={form.licenses}
                      aria-invalid={!!fieldErrors.licenses}
                      onChange={(event) => {
                        setForm((current) => ({ ...current, licenses: event.target.value }));
                        clearFieldError("licenses");
                      }}
                      placeholder="e.g. 20"
                    />
                    <FieldError message={fieldErrors.licenses} />
                  </div>

                  <div className="space-y-1">
                    <RequiredLabel htmlFor="grant-storage">Storage (GB)</RequiredLabel>
                    <Input
                      id="grant-storage"
                      type="number"
                      min="0"
                      value={form.dataLimitGB}
                      aria-invalid={!!fieldErrors.dataLimitGB}
                      onChange={(event) => {
                        setForm((current) => ({ ...current, dataLimitGB: event.target.value }));
                        clearFieldError("dataLimitGB");
                      }}
                      placeholder="e.g. 100"
                    />
                    <FieldError message={fieldErrors.dataLimitGB} />
                  </div>

                  <div className="space-y-1">
                    <RequiredLabel htmlFor="grant-duration">Duration (days)</RequiredLabel>
                    <Input
                      id="grant-duration"
                      type="number"
                      min="1"
                      value={form.durationDays}
                      aria-invalid={!!fieldErrors.durationDays}
                      onChange={(event) => {
                        setForm((current) => ({ ...current, durationDays: event.target.value }));
                        clearFieldError("durationDays");
                      }}
                      placeholder="e.g. 365"
                    />
                    <FieldError message={fieldErrors.durationDays} />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="grant-plan">Free Grant Plan</Label>
                  <Select
                    value={form.freeGrantPlanId || "none"}
                    onValueChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        freeGrantPlanId: value === "none" ? "" : value,
                      }))
                    }
                  >
                    <SelectTrigger id="grant-plan">
                      <SelectValue placeholder="No specific plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific plan</SelectItem>
                      {freeGrantPlanOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <Label>Discount Type</Label>
                  <RadioGroup
                    className="flex gap-6"
                    value={form.discountType}
                    onValueChange={(value) =>
                      setForm((current) => ({ ...current, discountType: value }))
                    }
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="%" id="discount-percent" />
                      <Label htmlFor="discount-percent" className="font-normal">
                        % Percentage
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="$" id="discount-amount" />
                      <Label htmlFor="discount-amount" className="font-normal">
                        $ Fixed Amount
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {form.discountType === "%" ? (
                  <div className="space-y-1">
                    <RequiredLabel htmlFor="discount-percent-value">Discount Percent</RequiredLabel>
                    <Input
                      id="discount-percent-value"
                      type="number"
                      min="0"
                      max="100"
                      value={form.discountPercent}
                      aria-invalid={!!fieldErrors.discountPercent}
                      onChange={(event) => {
                        setForm((current) => ({ ...current, discountPercent: event.target.value }));
                        clearFieldError("discountPercent");
                      }}
                      placeholder="e.g. 25"
                    />
                    <FieldError message={fieldErrors.discountPercent} />
                  </div>
                ) : (
                  <div className="space-y-1">
                    <RequiredLabel htmlFor="discount-amount-value">Discount Amount ($)</RequiredLabel>
                    <Input
                      id="discount-amount-value"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.discountAmount}
                      aria-invalid={!!fieldErrors.discountAmount}
                      onChange={(event) => {
                        setForm((current) => ({ ...current, discountAmount: event.target.value }));
                        clearFieldError("discountAmount");
                      }}
                      placeholder="e.g. 10"
                    />
                    <FieldError message={fieldErrors.discountAmount} />
                  </div>
                )}
              </>
            )}

            <div className="space-y-1">
              <Label htmlFor="coupon-usage-limit">Organization Usage Limit</Label>
              <Input
                id="coupon-usage-limit"
                type="number"
                min="0"
                value={form.usage_limit}
                onChange={(event) =>
                  setForm((current) => ({ ...current, usage_limit: event.target.value }))
                }
                placeholder="Leave blank for unlimited"
              />
              <p className="text-xs text-muted-foreground">
                Maximum number of organizations that can use this coupon.
              </p>
            </div>

            <div className="space-y-1">
              <Label>Thumbnail</Label>
              <input
                ref={thumbnailInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleThumbnailChange}
              />
              <div className="flex items-center gap-3">
                {form.thumbnailPath ? (
                  <img
                    src={form.thumbnailPath}
                    alt="Coupon thumbnail"
                    className="h-16 w-16 rounded border object-cover"
                  />
                ) : (
                  <div className="h-16 w-16 rounded border flex items-center justify-center text-muted-foreground bg-muted">
                    <ImageOff className="h-5 w-5" />
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploadingThumbnail}
                  onClick={() => thumbnailInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingThumbnail
                    ? "Uploading..."
                    : form.thumbnailPath
                      ? "Replace image"
                      : "Upload image"}
                </Button>
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="coupon-message">Coupon Message</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewOpen(true)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
              </div>
              <Textarea
                id="coupon-message"
                value={form.couponMessage}
                onChange={(event) =>
                  setForm((current) => ({ ...current, couponMessage: event.target.value }))
                }
                placeholder="Message shown to the user when the coupon is applied"
              />
            </div>

            {!isFreeGrant && (
              <div className="space-y-3 rounded-md border p-3">
                <p className="text-sm font-medium">Rules</p>

                <div className="space-y-1">
                  <Label>Applicable Plans</Label>
                  <MultiSelect
                    options={planOptions}
                    selected={form.rulesApplicablePlans}
                    onChange={(values) =>
                      setForm((current) => ({ ...current, rulesApplicablePlans: values }))
                    }
                    placeholder="All plans"
                    searchPlaceholder="Search plans..."
                    emptyText="No plans found."
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="coupon-per-user-limit">Per-user Limit</Label>
                    <Input
                      id="coupon-per-user-limit"
                      type="number"
                      min="0"
                      value={form.rulesPerUserLimit}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, rulesPerUserLimit: event.target.value }))
                      }
                      placeholder="Unlimited"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="coupon-min-order">Minimum Order Value ($)</Label>
                    <Input
                      id="coupon-min-order"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.rulesMinimumOrderValue}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          rulesMinimumOrderValue: event.target.value,
                        }))
                      }
                      placeholder="No minimum"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={isCreate ? onBack : () => goView()}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : isCreate ? "Create Coupon" : "Save Changes"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Card className={TAB_ACCENTS.coupons.border}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                {coupon.discountType === "$" ? (
                  <DollarSign className="h-4 w-4" />
                ) : (
                  <Percent className="h-4 w-4" />
                )}
                Discount &amp; Usage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DetailRow label="Discount" value={discountDisplay} />
              <DetailRow
                label="Usage"
                value={`${coupon.limit_used ?? 0} ${
                  coupon.usage_limit != null ? `/ ${coupon.usage_limit}` : "/ ∞"
                }`}
              />
              <DetailRow label="Starts At" value={formatDateTime(coupon.starts_at)} />
              <DetailRow label="Expires At" value={formatDateTime(coupon.expires_at)} />
            </CardContent>
          </Card>

          <Card className={TAB_ACCENTS.coupons.border}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="h-4 w-4" />
                Scope
              </CardTitle>
            </CardHeader>
            <CardContent>
              {organizationIds.length === 0 ? (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">Global</Badge>
                  <span className="text-sm text-muted-foreground">
                    Usable by every organization
                  </span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {organizationIds.map((id) => (
                    <Badge key={id} variant="outline">
                      {orgNames[id] || id}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={TAB_ACCENTS.coupons.border}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4" />
                Rules
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DetailRow
                label="Only logged-in users"
                value={rules.onlyForLoggedInUsers ? "Yes" : "No"}
              />
              <DetailRow label="One per user" value={rules.onePerUser ? "Yes" : "No"} />
              <DetailRow
                label="First purchase only"
                value={rules.firstPurchaseOnly ? "Yes" : "No"}
              />
              <DetailRow label="Per-user limit" value={rules.perUserLimit ?? "—"} />
              <DetailRow
                label="Minimum order value"
                value={rules.minimumOrderValue != null ? `$${rules.minimumOrderValue}` : "—"}
              />
              <DetailRow
                label="Applicable plans"
                value={
                  applicablePlans.length === 0 ? (
                    "All plans"
                  ) : (
                    <div className="flex flex-wrap gap-1 justify-end">
                      {applicablePlans.map((id) => (
                        <Badge key={id} variant="outline" className="text-xs">
                          {planNames[id] || id}
                        </Badge>
                      ))}
                    </div>
                  )
                }
              />
            </CardContent>
          </Card>

          {coupon.isFreeGrant && (
            <Card className={TAB_ACCENTS.coupons.border}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Gift className="h-4 w-4" />
                  Free Grant
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DetailRow label="Target Audience" value={coupon.targetAudience || "—"} />
                <DetailRow label="Licenses" value={coupon.licenses ?? "—"} />
                <DetailRow label="Data Limit (GB)" value={coupon.dataLimitGB ?? "—"} />
                <DetailRow label="Duration (days)" value={coupon.durationDays ?? "—"} />
                <DetailRow
                  label="Free Grant Plan"
                  value={
                    coupon.freeGrantPlanId
                      ? planNames[coupon.freeGrantPlanId] || coupon.freeGrantPlanId
                      : "—"
                  }
                />
                <DetailRow
                  label="Redeemed by"
                  value={`${(coupon.redeemedByOrgIds || []).length} organization(s)`}
                />
              </CardContent>
            </Card>
          )}

          {coupon.shareLink && (
            <Card className={TAB_ACCENTS.coupons.border}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Share2 className="h-4 w-4" />
                  Share Link
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted rounded px-2 py-1.5 truncate">
                    {coupon.shareLink}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(coupon.shareLink);
                        toast.success("Share link copied");
                      } catch {
                        toast.error("Failed to copy link");
                      }
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {(coupon.couponMessage || coupon.thumbnailPath) && (
            <Card className={TAB_ACCENTS.coupons.border}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="h-4 w-4" />
                  Coupon Message
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {coupon.thumbnailPath && (
                  <img
                    src={coupon.thumbnailPath}
                    alt="Coupon thumbnail"
                    className="h-24 w-24 rounded border object-cover"
                  />
                )}
                {coupon.couponMessage && <p className="text-sm">{coupon.couponMessage}</p>}
              </CardContent>
            </Card>
          )}

          <Card className={TAB_ACCENTS.coupons.border}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Calendar className="h-4 w-4" />
                Metadata
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DetailRow label="Created" value={formatDate(coupon.createdDate)} />
              <DetailRow label="Last Updated" value={formatDate(coupon.updatedAt)} />
              <DetailRow label="Created By" value={coupon.createdBy || "—"} />
            </CardContent>
          </Card>
        </div>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Coupon Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{" "}
              <strong>{coupon?.orgcoupon_name}</strong>?
              {coupon?.limit_used > 0 && (
                <>
                  {" "}This coupon has already been used {coupon.limit_used} time
                  {coupon.limit_used === 1 ? "" : "s"} — deletion may be blocked. Consider
                  disabling it instead from the coupons list.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CouponPreview
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        couponName={form.orgcoupon_name}
        couponCode={form.coupon_code}
        thumbnailUrl={form.thumbnailPath}
        message={form.couponMessage}
        description={form.description}
        isFreeGrant={isFreeGrant}
        discountType={form.discountType}
        discountPercent={form.discountPercent ? Number(form.discountPercent) : 0}
        discountAmount={form.discountAmount ? Number(form.discountAmount) : 0}
        licenses={form.licenses}
        dataLimitGB={form.dataLimitGB}
        durationDays={form.durationDays}
        startsAt={fromDatetimeLocal(form.starts_at)}
        expiresAt={fromDatetimeLocal(form.expires_at)}
        usageLimit={form.usage_limit === "" ? null : Number(form.usage_limit)}
        limitUsed={coupon?.limit_used}
        organizationIds={form.organizationIds}
        disabled={coupon?.disabled}
        perUserLimit={form.rulesPerUserLimit === "" ? undefined : Number(form.rulesPerUserLimit)}
        minimumOrderValue={
          form.rulesMinimumOrderValue === "" ? undefined : Number(form.rulesMinimumOrderValue)
        }
      />
    </div>
  );
}
